<template>
  <section>
    <!-- LOADER -->
    <Loader
      :loader="formLoader"
      label="Carregando..."
      loaderType="progress"
      loaderSize="h-96"
    />
    <form
      v-if="!formLoader"
      @submit.prevent="submitMaterial()"
      class="grid grid-cols-12 items-center gap-x-3"
    >
      <div class="flex justify-between items-center col-span-12">
        <p class="text-xl md:text-2xl font-bold">
          {{ $route.params.id ? $t("objects.edit") : $t("objects.add") }}
        </p>
        <div class="flex items-center gap-2">
          <Button
            :label="$t('labels.cancel')"
            class="btn-outline p-button-sm"
            @click="confirmModal()"
          />
          <Button
            :label="$t('labels.save')"
            class="btn-primary p-button-sm"
            type="submit"
          />
        </div>
      </div>

      <div class="flex flex-col my-4 col-span-6" v-if="isAdaptative">
        <p :class="`mb-2`">{{ $t("objects.inputs.classification") }}</p>
        <Dropdown
          id="learning-input"
          v-model="form.material.learningGroupType"
          :options="learningTypes"
          optionLabel="name"
          optionValue="code"
          :placeholder="$t('objects.inputs.select')"
          :class="v$.form.material.learningGroupType.$error ? 'p-invalid' : ''"
        />
        <Errors :errors="v$.form.material.learningGroupType.$errors" />
      </div>
      <div class="flex flex-col my-4 col-span-6" v-if="isAdaptative">
        <p :class="`mb-2`">{{ $t("objects.inputs.time") }}</p>
        <InputMask
          id="duration-input"
          mask="99:99:99"
          v-model="form.material.expectedConclusionTime"
          placeholder="00:00:00"
          :class="v$.form.material.expectedConclusionTime ? 'p-invalid' : ''"
        />
      </div>

      <div class="my-4 col-span-12">
        <p class="mb-2">{{ $t("objects.inputs.name") }}</p>
        <InputText
          id="title-input"
          type="text"
          name="name"
          v-model="form.material.name"
          :class="v$.form.material.name.$error ? 'p-invalid' : ''"
          :maxlength="50"
        />
        <Errors
          :errors="v$.form.material.name.$errors"
          :actualLength="form.material.name?.length"
          :maxLength="50"
        />
      </div>
      <div class="my-4 col-span-12">
        <p class="mb-2">
          {{ $t("objectives.inputs.description") }}
          {{ $t("labels.generic", { pt: "Opcional", en: "Optional" }) }}
        </p>
        <Textarea
          id="description-input"
          :autoResize="true"
          rows="10"
          cols="30"
          v-model="form.material.description"
          :maxlength="999"
        />
        <div class="flex justify-end">
          <p class="text-xs">{{ form.material.description?.length }}/999</p>
        </div>
      </div>
      <div v-if="$route.params.type == 'add'" class="my-4 col-span-12">
        <p class="mb-2">
          {{
            $t("labels.generic", {
              pt: "Fazer upload de novo conteúdo",
              en: "Upload new content",
            })
          }}
        </p>
        <Dropzone
          :files="form.files"
          :errors="v$.form.files.$errors"
          :btnLabel="$t('labels.select')"
        />
      </div>
    </form>
    <ConfirmDialog />
  </section>
</template>

<script lang="ts">
import { required } from "@/locale/locale.config";
import { Material, MaterialLearningGroupType } from "@/models/material.model";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { defineComponent } from "@vue/runtime-core";
import useVuelidate from "@vuelidate/core";
import moment from "moment";
import { delay } from "rxjs";

export default defineComponent({
  name: "newMaterialForm",
  computed: {
    rest(): MaterialsRest {
      return new MaterialsRest();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  data() {
    return {
      form: { material: new Material(), files: [] as any },
      courseId: String(this.$route.params.courseId),
      isAdaptative: this.$route.name === "formObject" ? true : false,
      autocomplete: {
        suggestions: [
          { name: "Swift", id: "1" },
          { name: "Mobile", value: "2" },
        ],
        selected: null,
      } as any,
      related: {
        suggestions: [],
        selected: null,
      } as any,
      learningTypes: [
        {
          name: this.$t("objects.type.motivation"),
          code: MaterialLearningGroupType.MOTIVATION,
        },
        {
          name: this.$t("objects.type.intro"),
          code: MaterialLearningGroupType.INTRODUCTION,
        },
        {
          name: this.$t("objects.type.dev"),
          code: MaterialLearningGroupType.DEVELOPMENT,
        },
        {
          name: this.$t("objects.type.closure"),
          code: MaterialLearningGroupType.CLOSURE,
        },
      ] as any,
      formLoader: false as boolean,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        material: {
          name: { required },
          learningGroupType: this.isAdaptative ? { required } : "",
        },
        files: this.$route.params.id ? "" : { required },
      },
    };
  },
  mounted() {
    this.getMaterialById();
  },
  methods: {
    confirmModal(): void {
      this.$confirm.require({
        message: "Deseja sair da tela de criação de material?",
        header: `Sair da tela`,
        acceptLabel: "Sair",
        rejectLabel: "Não",
        rejectClass: "btn-simple",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.$router.go(-1);
        },
      });
    },
    submitMaterial(): void {
      this.v$.form.$touch();
      if (!this.v$.form.$error) {
        this.formLoader = true;
        const materialId: any = this.$route.params.id;
        const data = new Material();
        data.id = undefined as any;
        data.name = this.form.material?.name;
        data.index = this.form.material.index;
        data.description = this.form.material?.description;
        data.learningGroupType = this.form.material?.learningGroupType;
        data.expectedConclusionTime = this.timeToSeconds(
          this.form.material.expectedConclusionTime
        );
        if (materialId) {
          this.update(data);
        } else {
          this.create(data);
        }
      }
    },
    create(data: Material): void {
      const objectiveId: any = this.$route.params.objectiveId;

      const appendName: any = { file: "file", data: "material" };
      const formData: FormData = this.generics.fileBuild(
        data,
        this.form.files[0],
        appendName
      );
      if (this.isAdaptative) {
        this.rest.postMaterial(objectiveId, formData).subscribe({
          next: () => {
            this.$router.go(-1);
          },
          error: () => {
            this.formLoader = false;
          },
        });
      } else {
        this.rest
          .postMaterialTraditional(this.courseId, objectiveId, formData)
          .subscribe({
            next: () => {
              this.$router.go(-1);
            },
            error: () => {
              this.formLoader = false;
            },
          });
      }
    },
    update(data: Material): void {
      const objectiveId: any = this.$route.params.objectiveId;
      const materialId: any = this.$route.params.id;
      this.rest
        .updateMaterial(objectiveId, materialId, data)
        .pipe(delay(1500))
        .subscribe({
          next: (response) => {
            this.$router.go(-1);
          },
          error: () => {
            this.formLoader = false;
          },
        });
    },
    getMaterialById(): void {
      const objectiveId: any = this.$route.params.objectiveId;
      const materialId: any = this.$route.params.id;
      if (materialId) {
        this.formLoader = true;
        this.rest
          .getMaterialById(objectiveId, materialId)
          .pipe(delay(250))
          .subscribe({
            next: (response) => {
              this.form.material = response;
              this.form.material.expectedConclusionTime = moment
                .utc(response.expectedConclusionTime * 1000)
                .format("HH:mm:ss");
              this.formLoader = false;
            },
            error: () => {
              this.$router.go(-1);
            },
          });
      }
    },
    searchForContent(event: any): void {
      const { query } = event;
      this.autocomplete.suggestions = this.autocomplete.suggestions.filter(
        (e: any) => {
          return e.name.toLowerCase().includes(query.toLowerCase());
        }
      );
    },
    addNewContent(event: any): void {
      var { value } = event.target;
      var obj: any = { name: value };
      if (value?.length !== 0) {
        var filtered = this.form.material?.content?.filter(
          (e: any) => e?.name == value
        );
        if (filtered?.length == 1) {
          this.$toast.add(ToastFactory.warn(Messages.CATEGORIE_DUPLICATED));
        } else {
          this.form.material?.content.push(obj);
        }
        event.target.value = "";
      }
    },
    timeToSeconds(timeString: string): number {
      const timeFormat: any = "HH:mm:ss";
      const duration = moment.duration(timeString, timeFormat);
      return duration.asSeconds();
    },
  },
});
</script>
